import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Modal, Spinner, SectionHeader } from '../../components/shared/UI';
import API from '../../utils/apiClient';

// ── Helpers ───────────────────────────────────────────────────
function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

function StatusBadge({ status }) {
  const map = {
    paid:    'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    failed:  'bg-red-100 text-red-600',
    manual:  'bg-blue-100 text-blue-700',
    overdue: 'bg-orange-100 text-orange-600',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-500';
  const labels = { paid:'✓ Paid', pending:'⏳ Pending', failed:'✗ Failed', manual:'Manual', overdue:'Overdue' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${cls}`}>
      {labels[status] || status}
    </span>
  );
}

function MethodBadge({ method }) {
  if (!method || method === '—') return <span className="text-xs text-gray-400">—</span>;
  const icons = { card:'💳', mobile_money:'📱', mobile:'📱', bank_transfer:'🏦', manual:'✍️', mock:'🧪' };
  const labels = { card:'Card', mobile_money:'Mobile Money', mobile:'Mobile Money', bank_transfer:'Bank Transfer', manual:'Manual', mock:'Test' };
  const icon  = icons[method]  || '💳';
  const label = labels[method] || method;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
      {icon} {label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────
export default function PaymentsPage() {
  const { addToast } = useToast();
  const [payments, setPayments]   = useState([]);
  const [summary, setSummary]     = useState({ totalRevenue:0, monthRevenue:0, pendingCount:0 });
  const [loading, setLoading]     = useState(true);
  const [confirming, setConfirm]  = useState(null);
  const [overrideModal, setOvrd]  = useState(null);
  const [overrideNote, setONote]  = useState('');
  const [overriding, setOverriding] = useState(false);
  const [levelFilter, setLevelF]  = useState('all');
  const [statusFilter, setStatusF] = useState('all');
  const [search, setSearch]       = useState('');

  const load = () => {
    setLoading(true);
    API.get('/payments')
      .then(({ data }) => {
        setPayments(data.payments || []);
        if (data.summary) setSummary(data.summary);
      })
      .catch(() => addToast('Failed to load payments', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleConfirm = async (paymentId, studentName) => {
    setConfirm(paymentId);
    try {
      await API.put(`/payments/${paymentId}/confirm`);
      addToast(`Payment confirmed for ${studentName}. Level unlocked!`, 'success');
      setPayments(prev => prev.map(p =>
        p.id === paymentId ? { ...p, status:'paid', date_paid: new Date().toISOString() } : p
      ));
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setConfirm(null); }
  };

  const handleOverride = async () => {
    if (!overrideModal) return;
    setOverriding(true);
    try {
      await API.post(`/super-admin/payment-override`, {
        studentId: overrideModal.student_id,
        level:     overrideModal.level,
        note:      overrideNote || 'Scholarship / manual override',
      });
      addToast('Payment overridden — level unlocked.', 'success');
      setOvrd(null); setONote('');
      load();
    } catch (err) { addToast(err.message || 'Override failed', 'error'); }
    finally { setOverriding(false); }
  };

  // Apply filters
  const filtered = payments.filter(p => {
    if (levelFilter !== 'all' && p.level !== levelFilter) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.student_name?.toLowerCase().includes(q) ||
             p.student_email?.toLowerCase().includes(q) ||
             p.student_number?.toLowerCase().includes(q) ||
             p.paystack_reference?.toLowerCase().includes(q);
    }
    return true;
  });

  const pending  = payments.filter(p => p.status === 'pending').length;
  const revenue  = parseFloat(summary.totalRevenue || 0);
  const mRevenue = parseFloat(summary.monthRevenue  || 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

      <SectionHeader title="Payments" subtitle="Manage course fee payments and level access" />

      {/* ── Summary cards ────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label:'Total Revenue',    value:`GHS ${revenue.toLocaleString('en-GH', { minimumFractionDigits:2 })}`,  icon:'💰', cls:'bg-green-50 text-green-700'  },
          { label:'This Month',       value:`GHS ${mRevenue.toLocaleString('en-GH', { minimumFractionDigits:2 })}`, icon:'📅', cls:'bg-blue-50 text-blue-700'    },
          { label:'Pending',          value: pending,         icon:'⏳', cls:'bg-yellow-50 text-yellow-700' },
          { label:'Total Payments',   value: payments.length, icon:'📋', cls:'bg-brand-50 text-brand-600'  },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="text-2xl mb-2">{s.icon}</div>
            <p className={`text-xl font-bold ${s.cls.split(' ')[1]}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Pending payments banner ───────────────────────────── */}
      {pending > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⏳</span>
            <div>
              <p className="text-sm font-bold text-yellow-800">{pending} pending payment{pending !== 1 ? 's' : ''}</p>
              <p className="text-xs text-yellow-700">These students are waiting for manual confirmation.</p>
            </div>
          </div>
          <button onClick={() => setStatusF('pending')}
            className="px-3 py-1.5 bg-yellow-600 text-white text-xs font-bold rounded-lg hover:bg-yellow-700 transition-colors whitespace-nowrap">
            View All →
          </button>
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────── */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="flex-1 min-w-48">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, ID or reference…"
              className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
          </div>
          {/* Status filter */}
          <div className="flex gap-1 p-1 bg-surface rounded-xl">
            {['all','paid','pending','failed'].map(s => (
              <button key={s} onClick={() => setStatusF(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all
                  ${statusFilter === s ? 'bg-white text-brand-500 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
          {/* Level filter */}
          <div className="flex gap-1 p-1 bg-surface rounded-xl">
            {['all','A1','A2','B1','B2'].map(l => (
              <button key={l} onClick={() => setLevelF(l)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                  ${levelFilter === l ? 'bg-white text-brand-500 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Spinner className="text-brand-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            {search || levelFilter !== 'all' || statusFilter !== 'all'
              ? 'No payments match your filters.'
              : 'No payment records yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Student ID','Student','Level','Amount','Method','Reference','Status','Date','Actions'].map(h => (
                    <th key={h} className="text-left py-2.5 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr key={p.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${i % 2 ? 'bg-gray-50/40' : ''}`}>
                    {/* Student ID */}
                    <td className="py-2.5 px-4 font-mono text-xs font-bold text-brand-500 whitespace-nowrap">
                      {p.student_number || '—'}
                    </td>
                    {/* Name + email */}
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {p.student_name?.charAt(0) || '?'}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-de-black text-xs truncate">{p.student_name}</p>
                          <p className="text-gray-400 text-xs truncate">{p.student_email}</p>
                        </div>
                      </div>
                    </td>
                    {/* Level */}
                    <td className="py-2.5 px-4">
                      <span className="px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-bold">{p.level}</span>
                    </td>
                    {/* Amount */}
                    <td className="py-2.5 px-4 text-xs font-bold text-de-black whitespace-nowrap">
                      GHS {parseFloat(p.amount || 0).toLocaleString('en-GH', { minimumFractionDigits:2 })}
                    </td>
                    {/* Method */}
                    <td className="py-2.5 px-4">
                      <MethodBadge method={p.payment_method} />
                    </td>
                    {/* Reference */}
                    <td className="py-2.5 px-4">
                      {p.paystack_reference ? (
                        <span className="font-mono text-xs text-gray-500 block max-w-[120px] truncate" title={p.paystack_reference}>
                          {p.paystack_reference.slice(0, 18)}…
                        </span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    {/* Status */}
                    <td className="py-2.5 px-4">
                      <StatusBadge status={p.status} />
                    </td>
                    {/* Date */}
                    <td className="py-2.5 px-4 text-xs text-gray-500 whitespace-nowrap">
                      {fmt(p.date_paid || p.created_at)}
                    </td>
                    {/* Actions */}
                    <td className="py-2.5 px-4">
                      <div className="flex gap-1.5">
                        {p.status !== 'paid' && (
                          <Button size="sm" variant="success"
                            loading={confirming === p.id}
                            onClick={() => handleConfirm(p.id, p.student_name)}>
                            Confirm
                          </Button>
                        )}
                        <Button size="sm" variant="secondary"
                          onClick={() => { setOvrd(p); setONote(''); }}>
                          Override
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals footer */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <p className="text-xs text-gray-500">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</p>
              <p className="text-xs font-bold text-de-black">
                Showing GHS{' '}
                {filtered
                  .filter(p => p.status === 'paid')
                  .reduce((s, p) => s + parseFloat(p.amount || 0), 0)
                  .toLocaleString('en-GH', { minimumFractionDigits:2 })}
                {' '}confirmed
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* ── Override Modal ─────────────────────────────────────── */}
      <Modal isOpen={!!overrideModal} onClose={() => setOvrd(null)} title="Payment Override" size="sm">
        {overrideModal && (
          <div className="space-y-4">
            <div className="p-3 bg-surface rounded-xl text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Student</span>
                <span className="font-semibold">{overrideModal.student_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Level</span>
                <span className="font-bold text-brand-500">{overrideModal.level}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Amount</span>
                <span>GHS {parseFloat(overrideModal.amount || 0).toLocaleString('en-GH', { minimumFractionDigits:2 })}</span>
              </div>
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-800">
              ⚠️ This will mark the payment as paid and immediately unlock Level {overrideModal.level}
              without requiring actual payment. Use for scholarship students or cash payments.
            </div>

            <div>
              <label className="block text-xs font-bold text-de-black mb-1.5">Reason / Note</label>
              <input type="text" value={overrideNote} onChange={e => setONote(e.target.value)}
                placeholder="e.g. Scholarship student / Cash payment received"
                className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setOvrd(null)}>Cancel</Button>
              <Button variant="primary"   size="sm" loading={overriding} onClick={handleOverride}>
                Override & Unlock
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
